import { useCallback } from 'react'
import { parseContentDispositionFilename } from '@/utils/contentDisposition'
import type {
  DatabaseInvoice,
  ColumnDefinition,
  InvoiceInput,
  InvoiceStatus,
  PaginatedResponse,
  DatabaseFilterParams,
  DatabaseStats,
  TrashListResponse,
  RestoreInvoiceResponse,
  PurgeResult,
} from '@/types/database'

const API_URL = import.meta.env.VITE_API_URL || '/v1'

/** Export failure. status 0 = network failure. serverMessage is for logging only, never rendered. */
export class ExportError extends Error {
  status: number
  serverMessage: string
  constructor(status: number, serverMessage: string) {
    super(`Export failed (${status})`)
    this.name = 'ExportError'
    this.status = status
    this.serverMessage = serverMessage
  }
}

export function useDatabase() {
  const loading = false
  const error = null

  // Helper function for API calls
  const apiCall = useCallback(async <T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<T> => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(error.error || error.message || 'Request failed')
    }

    if (response.status === 204) {
      return undefined as T
    }

    const text = await response.text()
    if (!text) {
      return undefined as T
    }

    return JSON.parse(text) as T
  }, [])

  // Get all invoices with pagination
  const getInvoices = useCallback(
    async (params: {
      page?: number
      limit?: number
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
      filters?: DatabaseFilterParams
    } = {}
  ): Promise<PaginatedResponse<DatabaseInvoice>> => {
      const queryParams = new URLSearchParams()
      if (params.page) queryParams.append('page', String(params.page))
      if (params.limit) queryParams.append('limit', String(params.limit))
      if (params.sortBy) queryParams.append('sortBy', params.sortBy)
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder)
      if (params.filters?.supplierName) queryParams.append('supplierName', params.filters.supplierName)
      if (params.filters?.dateFrom) queryParams.append('dateFrom', params.filters.dateFrom)
      if (params.filters?.dateTo) queryParams.append('dateTo', params.filters.dateTo)
      if (params.filters?.source) queryParams.append('source', params.filters.source)

      return apiCall<PaginatedResponse<DatabaseInvoice>>(
        `/database/invoices?${queryParams.toString()}`
      )
    },
    [apiCall]
  )

  // Get single invoice
  const getInvoiceById = useCallback(
    async (id: string): Promise<DatabaseInvoice> => {
      return apiCall<DatabaseInvoice>(`/database/invoices/${id}`)
    },
    [apiCall]
  )

  // Create new invoice
  const createInvoice = useCallback(
    async (input: InvoiceInput): Promise<DatabaseInvoice> => {
      return apiCall<DatabaseInvoice>('/database/invoices', {
        method: 'POST',
        body: JSON.stringify(input),
      })
    },
    [apiCall]
  )

  // Create multiple invoices
  const createInvoices = useCallback(
    async (inputs: InvoiceInput[]): Promise<DatabaseInvoice[]> => {
      return apiCall<DatabaseInvoice[]>('/database/invoices/batch', {
        method: 'POST',
        body: JSON.stringify(inputs),
      })
    },
    [apiCall]
  )

  // Update existing invoice
  const updateInvoice = useCallback(
    async (id: string, input: Partial<InvoiceInput>): Promise<DatabaseInvoice> => {
      return apiCall<DatabaseInvoice>(`/database/invoices/${id}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      })
    },
    [apiCall]
  )

  // Bulk-update status (+ optional approver/approvalDate) for many invoices
  const bulkUpdateStatus = useCallback(
    async (
      ids: string[],
      status: InvoiceStatus,
      approver?: string | null,
      approvalDate?: string | null,
    ): Promise<{ updated: number }> => {
      return apiCall<{ updated: number }>('/database/invoices/bulk-status', {
        method: 'POST',
        body: JSON.stringify({ ids, status, approver, approvalDate }),
      })
    },
    [apiCall]
  )

  // Delete invoice
  const deleteInvoice = useCallback(
    async (id: string): Promise<void> => {
      await apiCall<void>(`/database/invoices/${id}`, { method: 'DELETE' })
    },
    [apiCall]
  )

  // Trash (soft delete): list, restore, purge
  const getTrash = useCallback(async (): Promise<TrashListResponse> => {
    return apiCall<TrashListResponse>('/database/trash')
  }, [apiCall])

  const restoreInvoice = useCallback(
    async (id: string): Promise<RestoreInvoiceResponse> => {
      return apiCall<RestoreInvoiceResponse>(`/database/trash/${id}/restore`, { method: 'POST' })
    },
    [apiCall]
  )

  const purgeInvoice = useCallback(
    async (id: string): Promise<PurgeResult> => {
      return apiCall<PurgeResult>(`/database/trash/${id}`, { method: 'DELETE' })
    },
    [apiCall]
  )

  const purgeExpiredTrash = useCallback(async (): Promise<PurgeResult> => {
    return apiCall<PurgeResult>('/database/trash/purge-expired', { method: 'POST' })
  }, [apiCall])

  // Delete all invoices
  const deleteAllInvoices = useCallback(async (): Promise<number> => {
    const result = await apiCall<{ deleted: number }>('/database/invoices', {
      method: 'DELETE',
    })
    return result.deleted
  }, [apiCall])

  // Get database statistics
  const getStats = useCallback(async (): Promise<DatabaseStats> => {
    return apiCall<DatabaseStats>('/database/stats')
  }, [apiCall])

  // Get unique suppliers
  const getUniqueSuppliers = useCallback(async (): Promise<string[]> => {
    const result = await apiCall<{ suppliers: string[] }>('/database/suppliers')
    return result.suppliers
  }, [apiCall])

  // Check for duplicate invoices
  const checkDuplicate = useCallback(
    async (payload: {
      supplierName: string
      rechnungsnummer: string
      documentType?: string
    }): Promise<{
      isDuplicate: boolean
      duplicateCount: number
      duplicateIds: string[]
    }> => {
      return apiCall<{
        isDuplicate: boolean
        duplicateCount: number
        duplicateIds: string[]
      }>('/database/invoices/check-duplicate', {
        method: 'POST',
        body: JSON.stringify(payload),
      })
    },
    [apiCall]
  )

  // Get all custom columns
  const getColumns = useCallback(async (): Promise<ColumnDefinition[]> => {
    const result = await apiCall<{ columns: ColumnDefinition[] }>('/database/columns')
    return result.columns
  }, [apiCall])

  // Create custom column
  const createColumn = useCallback(
    async (name: string, type: 'text' | 'number' | 'date'): Promise<ColumnDefinition> => {
      return apiCall<ColumnDefinition>('/database/columns', {
        method: 'POST',
        body: JSON.stringify({ name, type }),
      })
    },
    [apiCall]
  )

  // Delete custom column
  const deleteColumn = useCallback(
    async (id: string): Promise<void> => {
      await apiCall<void>(`/database/columns/${id}`, { method: 'DELETE' })
    },
    [apiCall]
  )

  // Export invoices
  const exportInvoices = useCallback(
    async (config: { format: 'xlsx' | 'csv'; separator: 'comma' | 'period'; includeCustomColumns: boolean }): Promise<void> => {
      const queryParams = new URLSearchParams()
      queryParams.append('format', config.format)
      queryParams.append('separator', config.separator)
      if (!config.includeCustomColumns) {
        queryParams.append('includeCustomColumns', 'false')
      }

      let response: Response
      try {
        response = await fetch(`${API_URL}/database/export?${queryParams.toString()}`, {
          credentials: 'include',
        })
      } catch (err) {
        throw new ExportError(0, err instanceof Error ? err.message : 'Network error')
      }
      if (!response.ok) {
        let serverMessage = response.statusText
        try {
          const body = await response.json()
          serverMessage = body?.message ?? body?.error ?? serverMessage
        } catch {
          // body is not JSON: keep statusText
        }
        throw new ExportError(response.status, serverMessage)
      }

      // Get filename from Content-Disposition header or generate one
      const filename =
        parseContentDispositionFilename(response.headers.get('Content-Disposition')) ??
        `invoices.${config.format}`

      // Download the file
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => window.URL.revokeObjectURL(url), 1000)
    },
    []
  )

  // Import invoices from Excel
  const importFromExcel = useCallback(
    async (file: File): Promise<{ imported: number; invoices: DatabaseInvoice[] }> => {
      console.log('importFromExcel called with file:', file.name, 'size:', file.size, 'API_URL:', API_URL)
      const formData = new FormData()
      formData.append('file', file)

      console.log('Calling fetch to:', `${API_URL}/database/import/excel`)
      const response = await fetch(`${API_URL}/database/import/excel`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Import failed' }))
        throw new Error(error.error || 'Import failed')
      }

      return response.json()
    },
    []
  )

  return {
    loading,
    error,
    getInvoices,
    getInvoiceById,
    createInvoice,
    createInvoices,
    updateInvoice,
    bulkUpdateStatus,
    deleteInvoice,
    getTrash,
    restoreInvoice,
    purgeInvoice,
    purgeExpiredTrash,
    deleteAllInvoices,
    getStats,
    getUniqueSuppliers,
    checkDuplicate,
    getColumns,
    createColumn,
    deleteColumn,
    exportInvoices,
    importFromExcel,
  }
}
